"use client";

import { Box, Columns, Row, Text } from "@dxp-feed/ui";
import { useRef, useState } from "@/utils/hooks/reactUseCustomHooks";
import NearByStores from "@/templates/storeDetail/NearByStores";
import DepartmentsToggle from "@/templates/storeDetail/DepartmentsToggle";
import { IListStores } from "@/utils/redux/storeLocator";
import { IDepartment } from "@/utils/types/storeLocator";
import Link from "next/link";

interface IWhatsNewInStoreProps {
  store: IListStores | null;
  selectedStore: IListStores;
  openDialog: () => void;
  setStoreToConfirm: (store: IListStores) => void;
}

const WhatsNewInStore: React.FC<IWhatsNewInStoreProps> = ({
  store,
  selectedStore,
  openDialog,
  setStoreToConfirm,
}) => {
  const [activeDepartment, setActiveDepartment] = useState<string | null>(null);

  const toggleDepartment = (departmentName: string, canToggle: boolean) => {
    if (canToggle) {
      setActiveDepartment((prevState) =>
        prevState === departmentName ? null : departmentName
      );
    }
  };

  const departmentsClassArray = [
    { iconClass: "icon-car", label: "Parking" },
    { iconClass: "icon-bread", label: "Bakery Department" },
    { iconClass: "icon-bread", label: "Bakery" },
    { iconClass: "icon-noodles", label: "Sushi and Noodle Bars" },
    { iconClass: "icon-deli-department", label: "Deli Department" },
    { iconClass: "icon-meat-slice", label: "Meat Department" },
    { iconClass: "icon-orange", label: "Produce Department" },
    { iconClass: "icon-floral", label: "Floral Department" },
    { iconClass: "icon-seafood", label: "Seafood Department" },
    { iconClass: "icon-jug", label: "Cafe" },
    { iconClass: "icon-beer", label: "Beer & Wine Sales" },
    { iconClass: "icon-beer", label: "Beer Department" },
    { iconClass: "icon-pill", label: "Pharmacy" },
    { iconClass: "icon-spa", label: "Natural Source Department" },
    { iconClass: "icon-liquor", label: "Bulk Foods Department" },
  ];

  const departments: IDepartment[] =
    (store?.departments as IDepartment[]) || [];
  //we are getting some deparment name as html fomart (eg Beer &amp; Wine Sales)
  //so convert this into normal string
  const decodeHTML = (input: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(input, "text/html");
    return doc.documentElement.textContent || "";
  };
  // Removiong duplicates based on the 'name' property (getting duplicate department from api)
  const seenNames = new Set();
  if (departments.length > 0) {
    for (let i = 0; i < departments.length; i++) {
      if (seenNames.has(departments[i].type)) {
        departments.splice(i, 1);
        i--;
      } else {
        seenNames.add(departments[i].type);
      }
    }
  }


  const [activeIndex, setActiveIndex] = useState(0);
  const linkRefs = [useRef<HTMLAnchorElement>(null), useRef<HTMLAnchorElement>(null)];

  const handleClick = (index: number, e: React.MouseEvent<HTMLAnchorElement>) => {

    e.preventDefault();
    setActiveIndex(index);
    const id = index === 0 ? "WhatsinTheStore" : "nearbyStores";
    const element = document.getElementById(id);
    if (element) {
      let yOffset = 0;
      const headerTop = document.getElementsByTagName("header")?.[0];
      if (headerTop) {
        const headerTopHeight = headerTop.getBoundingClientRect().height || 0;
        yOffset -= headerTopHeight;
      }

      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }

  }

  return (
    <>
          <Row id="WhatsinTheStore">
            <Columns className="col-2 6xl:w-1/6 lg:w-1/5 w-full sm:flex flex-col lg:!pr-6 hidden">
              <Text as="h2" className="font-sans text-xl lg:pb-6 pb-4 font-bold">
                Jump to Section
              </Text>
              <Box as="div" className="lg:flex block lg:flex-col lg:pb-0 pb-6">
                {departments &&
                  departments.filter(
                    (department) =>
                      department.type && department.type.toLowerCase() !== "undefined"
                  ).length > 0 && (
                  <Link
                    href={"#WhatsinTheStore"}
                    ref={linkRefs[0]}
                    onClick={(e) => handleClick(0, e)}
                    className={`w-full lg:py-3 lg:px-6 lg:mr-0 mr-4 pr-4 border-r-[1px] border-r-grey350 text-left text-body lg:border lg:border-grey500 bg-brandWhite lg:border-r-0 relative !border-b-0 !border-b-transparent lg:before:absolute lg:before:left-0 lg:before:w-1 lg:before:h-full lg:before:bg-brandSecondary before:top-0 ${activeIndex === 0 ? "bg-active !text-brandSecondary" : "lg:bg-grey150 !text-brandBlack before:hidden"
                      }`}
                  >
                    What’s in the store
                  </Link>
                )}
                <Link
                  href={"#nearbyStores"}
                  ref={linkRefs[1]}
                  onClick={(e) => handleClick(1, e)}
                  className={`w-full lg:py-3 lg:px-6 text-left lg:border lg:border-grey500 text-body bg-brandWhite lg:border-r-0 relative lg:before:absolute lg:before:left-0 lg:before:w-1 lg:before:h-full lg:before:bg-brandSecondary lg:before:top-0 ${activeIndex === 1 || departments?.length < 1 ? "bg-active !text-brandSecondary" : "lg:bg-grey150 before:hidden !text-brandBlack"
                    }`}
                >
                  Nearby Store
                </Link>
              </Box>
            </Columns>

            <Columns className="col-10  6xl:w-5/6 lg:w-4/5 w-full lg:!pl-0">
              <div className="flex flex-col gap-6 lg:gap-12">
                <div>
                  {departments &&
                    departments.filter(
                      (department) =>
                        department.type && department.type.toLowerCase() !== "undefined"
                    ).length > 0 && (
                      <>
                        <Text as="h2" className="font-sans md:text-3xl text-2xl pb-6 font-extrabold">
                          What’s in the Store
                        </Text>
                        <p className="mb-6 text-body">
                          All In-Store Services and Departments
                        </p>
                     </>
                    )}
                  <Box
                    as="div"
                    className="grid gap-6 lgs:grid-cols-3 md:grid-cols-2"
                  >
                    {departments
                      .filter(
                        (department) =>
                          department.type &&
                          department.type.toLowerCase() !== "undefined"
                      )
                      .map((department, index) => {
                        const departmentClass = departmentsClassArray.find(
                          (dClass) => dClass.label === decodeHTML(department.type)
                        );
                        const isToggled = activeDepartment === department.type;

                        return (
                          <Box
                            key={index}
                            className={`relative flex gap-4 items-center pb-2 border-grey350 border-b-[1px] cursor-pointer ${isToggled ? "after:absolute after:bottom-[-1px] after:h-[2px] after:w-full after:bg-brandSecondary" : ""}`}
                            onClick={() =>
                              toggleDepartment(
                                decodeHTML(department.type),
                                department.showDepartmentDetails
                              )
                            }
                          >
                            <Text
                              as="span"
                              className={`${departmentClass?.iconClass || ""} text-brandBlack font-bold`}
                            />
                            <Text
                              as="p"
                              className="text-xl font-sans text-brandBlack font-bold"
                            >
                              {decodeHTML(department.type)}
                            </Text>
                            {department.showDepartmentDetails &&
                              departments.find(
                                (dept) =>
                                  dept.type === decodeHTML(department.type) &&
                                  (dept.showDepartmentDetails || isToggled)
                              ) && (
                                <Box
                                  as="div"
                                  className="shrink-0 ml-auto mr-3 text-[12px]"
                                  onClick={(e: any) => {
                                    e.stopPropagation();
                                    setActiveDepartment(
                                      isToggled ? null : decodeHTML(department.type)
                                    );
                                  }}
                                >
                                  {department.showDepartmentDetails &&
                                    departments.find(
                                      (dept) =>
                                        dept.type === decodeHTML(department.type) &&
                                        (dept.showDepartmentDetails || isToggled)
                                    ) ? (
                                    isToggled ? (
                                      <Text
                                        as="button"
                                        aria-label="close now"
                                        className="text-body font-bold underline text-brandSecondary"
                                      >
                                        Close
                                      </Text>
                                    ) : (
                                      <Text
                                        as="button"
                                        aria-label="Get info"
                                        className="text-body font-bold underline text-brandSecondary"
                                      >
                                        Get info
                                      </Text>
                                    )
                                  ) : null}
                                </Box>
                              )}
                          </Box>
                        );
                      })}
                  </Box>
                  {/* Rendering dropdown for the active department */}
                  {departments
                    .filter(
                      (department) =>
                        department.type &&
                        department.type.toLowerCase() !== "undefined"
                    )
                    .map((department) => {
                      const isToggled =
                        activeDepartment === decodeHTML(department.type);
                      return (
                        isToggled &&
                        department.showDepartmentDetails && (
                          <DepartmentsToggle
                            key={decodeHTML(department.type)}
                            selectedDepartment={department}
                            closeDepartment={() => setActiveDepartment(null)}
                          />
                        )
                      );
                    })}
                </div>

                {/* Near by stores component is always rendered */}
                <NearByStores
                  store={store}
                  selectedStore={selectedStore}
                  openDialog={openDialog}
                  setStoreToConfirm={setStoreToConfirm}
                />
              </div>
            </Columns>
          </Row>
    </>
  );
};

export default WhatsNewInStore;
